// zipcloud API（郵便番号検索）ユーティリティ
// https://zipcloud.ibsnet.co.jp/doc/api

interface ZipcloudResult {
  zipcode: string;
  prefcode: string;
  address1: string;  // 都道府県
  address2: string;  // 市区町村
  address3: string;  // 町域
}

interface ZipcloudResponse {
  status: number;
  message: string | null;
  results: ZipcloudResult[] | null;
}

export interface ZipcloudAddress {
  prefecture: string;
  city: string;
}

/**
 * 郵便番号から住所を検索する
 * @param zipcode ハイフン付きまたはなしの郵便番号（例: "150-0013" or "1500013"）
 * @returns 都道府県・市区町村 or null（該当なし/エラー）
 */
export async function searchAddressByZipcode(zipcode: string): Promise<ZipcloudAddress | null> {
  const cleanZip = zipcode.replace(/[-ー－]/g, '');
  if (!/^\d{7}$/.test(cleanZip)) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    const response = await fetch(
      `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${cleanZip}`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);

    if (!response.ok) {
      return null;
    }

    const data: ZipcloudResponse = await response.json();

    if (data.status !== 200 || !data.results || data.results.length === 0) {
      return null;
    }

    const result = data.results[0];
    return {
      prefecture: result.address1,
      city: result.address2 + result.address3,
    };
  } catch {
    return null;
  }
}

/**
 * 郵便番号のフォーマット（ハイフン自動挿入）
 * @param value 入力値
 * @returns フォーマット済み値
 */
export function formatPostalCode(value: string): string {
  const digits = value.replace(/[^\d]/g, '');
  if (digits.length <= 3) {
    return digits;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}`;
}
